import type { ReactNode } from "react";
import { Avatar } from "../../components";
import { publicSeatName, type PublicTableModel } from "../public-presentation";
/** Shared display / spectator composition. On a wide screen it is a TV board:
 * the public headline and evidence on the left, and a side rail with the acting
 * seat spotlighted, the public submission count in big numerals and the public
 * turn sequence when the engine establishes one. On narrow screens the rail
 * collapses to the compact "Up next" row. Inputs are the positive public model
 * only (DISCLOSURE): no private entry, draft or action is mounted here, and Mafia
 * night has no actor or count, so the rail is simply absent. */
export function PublicDisplay({
  board,
  context,
  model,
  progressLabel = "submitted",
  spotlight = true,
}: {
  board: ReactNode;
  context: ReactNode;
  model: PublicTableModel;
  progressLabel?: string;
  spotlight?: boolean;
}) {
  const next = model.nextIds.slice(0, 5);
  const actor = spotlight
    ? model.seats.find((s) => s.id === model.actorId)
    : undefined;
  const progress = spotlight ? model.progress : null;
  const side = Boolean(actor || progress || next.length);
  return (
    <div
      className={`public-display${next.length ? " has-next" : ""}${side ? " has-side" : ""}`}
    >
      <div className="public-main">
        {context}
        <section className="play-table" aria-label="Game board">
          {board}
        </section>
      </div>
      {side ? (
        <aside className="public-side">
          {actor ? (
            <section
              className={`public-spotlight${actor.connected === false ? " is-offline" : ""}`}
              aria-label="Up now"
            >
              <Avatar name={actor.name} index={actor.seat - 1} />
              <span>
                <small>
                  {actor.connected === false ? "Up now · offline" : "Up now"}
                </small>
                <strong>{publicSeatName(model.seats, actor.id)}</strong>
              </span>
            </section>
          ) : null}
          {progress ? (
            <section className="public-progress" aria-label="Progress">
              <strong>
                {progress.submitted}
                <span> / {progress.eligible}</span>
              </strong>
              <small>{progressLabel}</small>
            </section>
          ) : null}
          {next.length ? (
            <section className="public-next" aria-label="Up next">
              <h2>Up next</h2>
              <ol>
                {next.map((id, i) => {
                  const seat = model.seats.find((s) => s.id === id);
                  return (
                    <li key={id} className={i >= 3 ? "is-later" : undefined}>
                      <Avatar
                        name={seat?.name ?? "?"}
                        index={(seat?.seat ?? 1) - 1}
                        small
                      />
                      <span>{publicSeatName(model.seats, id)}</span>
                    </li>
                  );
                })}
              </ol>
            </section>
          ) : null}
        </aside>
      ) : null}
    </div>
  );
}
